import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import EnIcon from 'Components/icons/EnIcon.vue';
import { useIdBuilder } from '../composables/useIdBuilder';
import EnTooltip from 'Components/base/EnTooltip/EnTooltip.vue';
import { EnMenu, EnMenuItem, MenuSeparator } from 'Components/base/EnMenu';
import { EnButton } from 'Components/base/EnButton';
/**
 * Context menu for DataGrid rows. Provides actions like edit, delete, copy, and custom actions.
 * Used to display row-level actions in a dropdown menu.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnDatagridContextMenu',
    props: /*@__PURE__*/ _mergeModels({
        /**
         * Additional context menu actions.
         * Besides standard options like "Copy" or "Edit", it can include actions like "Download", "Send", etc.
         * Is displayed in the top section of the context menu.
         */
        additionalActions: (Array),
        /**
         * Main context menu actions.
         * Is displayed in the bottom section of the context menu.
         */
        contextMenuActions: (Array),
        /**
         * Whether the "Edit" option is shown.
         */
        allowEditing: Boolean,
        /**
         * Whether the "Delete" option is shown.
         */
        allowDeleting: Boolean,
        /**
         * Whether the "Copy" option is shown.
         */
        allowCopying: Boolean,
        /**
         * Tooltip text for the delete action.
         */
        deletePreventionTooltip: String,
        /**
         * The currently selected item in the context menu.
         */
        contextMenuItem: Object,
        /**
         * Callback to edit an item.
         */
        editItem: Function,
        /**
         * Function that returns true if editing should be prevented.
         */
        editPrevention: Function,
        /**
         * Function that returns true if deleting should be prevented.
         */
        deletePrevention: Function,
        /**
         * Callback to initiate delete.
         */
        startDeleteItem: Function,
        /**
         * Callback to copy an item.
         */
        copyItem: Function,
        /**
         * Function that shows context menu when the icon is clicked.
         */
        showContextMenuViaIcon: Function,
        /**
         * Key used to identify items.
         */
        dataGridKey: [String, (Array)],
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue'], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const { t } = useI18n();
        const model = _useModel(__props, "modelValue");
        function isActionDisabled(action, contextMenuItem) {
            return action?.disabled ? action.disabled(contextMenuItem) : false;
        }
        function resolveDisabledTooltip(action, contextMenuItem) {
            if (!action?.disabledTooltip)
                return undefined;
            return typeof action.disabledTooltip === 'function' ? action.disabledTooltip(contextMenuItem) : action.disabledTooltip;
        }
        const menuComponent = ref(false);
        const { buildIdForItem } = useIdBuilder();
        const emit = __emit;
        /**
         * Computed property for two-way binding modelValue
         */
        const item = computed({
            get() {
                return model.value;
            },
            set(value) {
                /**
                 * Triggers when the value has changed.
                 */
                emit('update:modelValue', value);
            },
        });
        /**
         * State to manage tooltip visibility.
         */
        const tooltipIsShown = ref(false);
        /**
         * Builds static IDs for context-menu buttons based on item keys.
         */
        function buildContextMenuId(item) {
            return buildIdForItem('context-menu-action-button', props.dataGridKey, item);
        }
        /**
         * Builds static IDs for edit and delete buttons based on item keys.
         */
        function buildEditId(item) {
            return buildIdForItem('edit-button', props.dataGridKey, item);
        }
        /**
         * Builds static ID for the delete button based on the item's delete key.
         */
        function buildDeleteId(item) {
            return buildIdForItem('delete-button', props.dataGridKey, item);
        }
        /**
         * Builds static ID for the delete button based on the item's delete key.
         */
        function buildId(item, contextMenuItem) {
            return item.id?.(contextMenuItem);
        }
        /**
         * This function checks if the item can be edited.
         */
        function cannotBeDeleted(item) {
            if (!props.deletePrevention) {
                return false;
            }
            return props.deletePrevention(item);
        }
        /**
         * This function checks if the item can be edited
         */
        function cannotBeEdited(item) {
            if (!props.editPrevention) {
                return false;
            }
            return props.editPrevention(item);
        }
        function executeEdit(item) {
            if (!cannotBeEdited(item)) {
                props.editItem(item);
            }
            toggleMenu(false);
        }
        function startCopy(item) {
            props.copyItem?.(item);
            toggleMenu(false);
        }
        function startDelete(item) {
            props.startDeleteItem?.(item);
            toggleMenu(false);
        }
        function startAction(action, contextMenuItem) {
            action?.onClick && action.onClick(contextMenuItem);
            toggleMenu(false);
        }
        /**
         *
         * This function returns the tooltip for the delete action.
         */
        function deleteTooltipComputed(item) {
            if (cannotBeDeleted(item)) {
                // if delete prevention is active, return the custom tooltip.
                return props.deletePreventionTooltip;
            }
        }
        /**
         * Determines whether any context menu should be shown
         * @returns {Boolean}
         */
        const shouldShowContextMenu = () => {
            return (props.contextMenuActions?.length ?? 0) > 0 || props.additionalActions || props.allowEditing || props.allowCopying || props.allowDeleting;
        };
        /**
         * Filters additional actions based on visibility and context menu item
         * It fixes unnecessary rendering and visual border issues in the context menu.
         */
        const filteredAdditionalActions = computed(() => {
            if (!props.additionalActions)
                return [];
            return props.additionalActions.filter((action) => action && (!action.visible || action.visible(props.contextMenuItem)));
        });
        /**
         * Section-visibility flags, used to decide which separators to render.
         * A separator should only appear between two sections that are both shown —
         * never after the last visible section (e.g. only "additional actions",
         * with no modify/main actions, should show no separator at all).
         */
        const hasAdditionalActions = computed(() => filteredAdditionalActions.value.length > 0);
        const hasModifyActions = computed(() => props.allowEditing || props.allowCopying || props.allowDeleting);
        const hasMainActions = computed(() => (props.contextMenuActions?.length ?? 0) > 0);
        /**
         * Handles the click event on the context menu icon.
         Hides the tooltip and shows the context menu via the provided callback.
         * @param item
         */
        function handleShowContextMenuClick(item) {
            tooltipIsShown.value = false;
            props.showContextMenuViaIcon?.(item);
        }
        function executeClick(actionItem, contextMenuItem) {
            actionItem.onClick(contextMenuItem);
            toggleMenu(false);
        }
        function toggleMenu(value) {
            menuComponent.value.setOpen(value);
        }
        const __returned__ = { props, t, model, isActionDisabled, resolveDisabledTooltip, menuComponent, buildIdForItem, emit, item, tooltipIsShown, buildContextMenuId, buildEditId, buildDeleteId, buildId, cannotBeDeleted, cannotBeEdited, executeEdit, startCopy, startDelete, startAction, deleteTooltipComputed, shouldShowContextMenu, filteredAdditionalActions, hasAdditionalActions, hasModifyActions, hasMainActions, handleShowContextMenuClick, executeClick, toggleMenu, EnIcon, EnTooltip, get EnMenu() { return EnMenu; }, get EnMenuItem() { return EnMenuItem; }, get MenuSeparator() { return MenuSeparator; }, get EnButton() { return EnButton; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
